import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/components/AuthProvider";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { ApiError } from "@/lib/api";
import { hubDevApiOrigin, hubApiUrl } from "@/lib/hubApiOrigin";
import { hubOperationalDataSourceRequestHeader } from "@/lib/hubDataSource";
import { mcpOAuthLog } from "@/lib/mcp-oauth-log";

interface AuthorizeDetails {
  client: {
    clientId: string;
    clientName: string;
    softwareId: string | null;
    softwareVersion: string | null;
    createdAt: string;
  };
  redirectUri: string;
  scopes: string[];
  state: string | null;
  codeChallenge: string;
  codeChallengeMethod: string;
}

function safeRedirectScheme(url: string): string | null {
  try {
    return new URL(url).protocol;
  } catch {
    return null;
  }
}

function isNativeAppOAuthRedirect(url: string): boolean {
  try {
    const protocol = new URL(url).protocol;
    return protocol !== "http:" && protocol !== "https:";
  } catch {
    return false;
  }
}

/** HTTP(S) redirects can follow automatically after Approve; native schemes cannot. */
function handOffOAuthRedirect(url: string): void {
  if (isNativeAppOAuthRedirect(url)) {
    return;
  }
  window.location.assign(url);
}

async function probeApiHealth(): Promise<void> {
  const url = hubApiUrl("/healthz");
  const started = performance.now();
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 5_000);
  try {
    const response = await fetch(url, { cache: "no-store", signal: controller.signal });
    mcpOAuthLog("consent_api_health", {
      url,
      status: response.status,
      elapsedMs: Math.round(performance.now() - started),
    });
  } catch (error) {
    mcpOAuthLog("consent_api_health_failed", {
      url,
      elapsedMs: Math.round(performance.now() - started),
      message: error instanceof Error ? error.message : String(error),
    });
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function mcpConsentFetch(
  accessToken: string,
  path: string,
  init: { method?: string; body?: unknown; signal?: AbortSignal },
): Promise<Response> {
  const url = hubApiUrl(path);
  const method = init.method ?? "GET";
  mcpOAuthLog("consent_http_start", { path, url, method });
  const started = performance.now();
  const response = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...hubOperationalDataSourceRequestHeader(),
      ...(init.body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    signal: init.signal,
  });
  mcpOAuthLog("consent_http_end", {
    path,
    url,
    method,
    status: response.status,
    elapsedMs: Math.round(performance.now() - started),
  });
  return response;
}

const SCOPE_DESCRIPTIONS: Record<string, string> = {
  "bom:read":
    "Read BoM templates, revisions, parse rules, initial rows, and run preview functions on real SKUs.",
  "bom:write_drafts":
    "Insert and edit rows on draft (unsubmitted, unapproved) BoM revisions only. Approved revisions stay locked. Cannot submit or approve revisions.",
  "manufacturing:read":
    "Read manufacturing work orders, production calendar, daily scans, alternates board, batches, device lookup, and related Megaventory-derived fields.",
  "manufacturing:write":
    "Create and update work orders (allocate, receive, return), refresh inventory ingest, and move alternates. Does not include Megaventory BOM rebuild.",
  "documents:read":
    "Read controlled documents, collections, change orders, transition notices, and drawing coverage gaps.",
  "shipping:read":
    "Read shipments, GOSI invoice details, inbound sources, and carrier list.",
  "wiki:read":
    "Search and read Outline wiki documents (requires Outline sign-in in the Wiki hub UI).",
  "clampqc:read":
    "Read clamp QC work orders, reporting rollups, and physical failure reason codes.",
  "accounting:read":
    "Read cash flow forecast, EOM reconciliation months, and accounts payable invoice headers.",
  "mrp:read":
    "Read MRP locations, components, SKUs, inventory, safety stock alerts, AML, purchase orders, and planning by quarter.",
  "manufacturing_management:read":
    "Read manufacturing management data: CM purchase orders, transfers, potting send drafts, forward demand.",
  "wireless:read":
    "Read wireless portal device registration (by serial or IMEI) and the wireless issue workspace. Uses live Wireless_Prod RDS when configured, otherwise the wireless_prod mirror on Supabase.",
  "quality:read":
    "Read quality cases, investigations, corrective action reports (CAR), and quality device episode lookup.",
  "erp:read":
    "Read ingest control, scheduler, ingest runs/events, findings, app settings, and sweep summaries.",
  "marketing:read":
    "Read marketing ZPL label templates (merged with the vendored label-service catalog).",
};

export function McpAuthorizeConsentPage() {
  const { session } = useAuth();
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState<"approve" | "deny" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  const params = useMemo(() => {
    return {
      client_id: searchParams.get("client_id") ?? "",
      redirect_uri: searchParams.get("redirect_uri") ?? "",
      scope: searchParams.get("scope") ?? "",
      state: searchParams.get("state") ?? "",
      code_challenge: searchParams.get("code_challenge") ?? "",
      code_challenge_method: searchParams.get("code_challenge_method") ?? "S256",
      resource: searchParams.get("resource") ?? "",
    };
  }, [searchParams]);

  useEffect(() => {
    mcpOAuthLog("consent_page_open", {
      clientId: params.client_id,
      redirectUri: params.redirect_uri,
      scopeCount: params.scope.split(/\s+/).filter(Boolean).length,
      apiOrigin: hubDevApiOrigin(),
      pageOrigin: window.location.origin,
    });
  }, [params]);

  const detailsQuery = useQuery({
    queryKey: ["mcp", "authorize-details", params, session?.user.id],
    enabled: Boolean(session?.access_token),
    queryFn: async () => {
      const accessToken = session?.access_token?.trim();
      if (!accessToken) {
        throw new Error("Your hub sign-in expired. Refresh this page and sign in again.");
      }
      const search = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v) search.set(k, v);
      }
      const path = `/api/mcp/authorize/details?${search.toString()}`;
      const response = await mcpConsentFetch(accessToken, path, { method: "GET" });
      const text = await response.text();
      let data: unknown = undefined;
      if (text.length > 0) {
        try {
          data = JSON.parse(text);
        } catch {
          data = text;
        }
      }
      if (!response.ok) {
        const message =
          data && typeof data === "object" && typeof (data as { error?: unknown }).error === "string"
            ? (data as { error: string }).error
            : `Request failed: ${response.status}`;
        throw new ApiError(response.status, message, data);
      }
      mcpOAuthLog("consent_details_ok", {
        clientId: params.client_id,
        clientName:
          data && typeof data === "object"
            ? (data as { client?: { clientName?: string } }).client?.clientName
            : undefined,
      });
      return data as AuthorizeDetails;
    },
  });

  // If the details endpoint returns an error with a redirectTo (e.g. unsupported_response_type
  // pointing back to the client), bounce automatically.
  useEffect(() => {
    if (!detailsQuery.error) return;
    mcpOAuthLog("consent_details_error", {
      clientId: params.client_id,
      message: detailsQuery.error instanceof Error ? detailsQuery.error.message : String(detailsQuery.error),
    });
    const e = detailsQuery.error as { body?: { redirectTo?: string } };
    if (e.body && typeof e.body.redirectTo === "string") {
      window.location.assign(e.body.redirectTo);
    }
  }, [detailsQuery.error]);

  async function decide(decision: "approve" | "deny") {
    const accessToken = session?.access_token?.trim();
    if (!accessToken) {
      setError("Your hub sign-in expired. Refresh this page and sign in again.");
      return;
    }

    mcpOAuthLog("consent_decide_begin", {
      decision,
      clientId: params.client_id,
      apiOrigin: hubDevApiOrigin(),
    });
    await probeApiHealth();

    setSubmitting(decision);
    setError(null);
    setRedirectTo(null);

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 30_000);
    try {
      const response = await mcpConsentFetch(accessToken, "/api/mcp/authorize/decide", {
        method: "POST",
        body: { ...params, decision },
        signal: controller.signal,
      });
      const text = await response.text();
      let data: unknown = undefined;
      if (text.length > 0) {
        try {
          data = JSON.parse(text);
        } catch {
          data = text;
        }
      }
      if (!response.ok) {
        const message =
          data && typeof data === "object" && typeof (data as { error?: unknown }).error === "string"
            ? (data as { error: string }).error
            : `Request failed: ${response.status}`;
        throw new ApiError(response.status, message, data);
      }
      const redirect =
        data && typeof data === "object" && typeof (data as { redirectTo?: unknown }).redirectTo === "string"
          ? (data as { redirectTo: string }).redirectTo
          : null;
      const handoffRequiresUserGesture =
        data &&
        typeof data === "object" &&
        (data as { handoffRequiresUserGesture?: unknown }).handoffRequiresUserGesture === true
          ? true
          : redirect !== null && isNativeAppOAuthRedirect(redirect);
      if (!redirect) {
        throw new Error("Authorization response did not include a redirect URL.");
      }
      setSubmitting(null);
      setRedirectTo(redirect);
      mcpOAuthLog("consent_decide_ok", {
        decision,
        clientId: params.client_id,
        handoffRequiresUserGesture,
      });
      if (handoffRequiresUserGesture) {
        mcpOAuthLog("consent_handoff_waiting_user_click", {
          clientId: params.client_id,
          redirectScheme: safeRedirectScheme(redirect),
        });
      } else {
        handOffOAuthRedirect(redirect);
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        mcpOAuthLog("consent_decide_timeout", { decision, clientId: params.client_id });
        setError(
          "The authorization request timed out after 30 seconds. Check the console for [integrator:mcp-oauth] lines and API logs for consent_decide_incoming. In dev, requests go directly to the API on port 8123.",
        );
      } else {
        mcpOAuthLog("consent_decide_error", {
          decision,
          clientId: params.client_id,
          message: err instanceof Error ? err.message : String(err),
        });
        setError(err instanceof Error ? err.message : String(err));
      }
      setSubmitting(null);
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  const handoffComplete = redirectTo !== null;

  return (
    <div className="mx-auto w-full max-w-xl px-6 py-10">
      <Card>
        <CardHeader>
          <CardTitle>
            {detailsQuery.data
              ? `Authorize ${detailsQuery.data.client.clientName}`
              : "Authorize MCP client"}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {detailsQuery.isLoading ? (
            <p className="text-sm text-slate-500">Loading authorization request&hellip;</p>
          ) : detailsQuery.error ? (
            <div className="text-sm text-rose-700">
              <p className="font-medium">Cannot authorize this client</p>
              <p className="mt-1">{(detailsQuery.error as Error).message}</p>
            </div>
          ) : detailsQuery.data ? (
            <div className="space-y-5">
              <div>
                <p className="text-sm text-slate-700">
                  This MCP client is requesting access to your BoM Rules surface on the SGS hub:
                </p>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  {detailsQuery.data.scopes.map((scope) => (
                    <li key={scope}>
                      <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs">
                        {scope}
                      </code>{" "}
                      {SCOPE_DESCRIPTIONS[scope] ?? "Custom scope."}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                <p>
                  <span className="font-medium text-slate-800">Client ID:</span>{" "}
                  <code className="font-mono">{detailsQuery.data.client.clientId}</code>
                </p>
                <p className="mt-1">
                  <span className="font-medium text-slate-800">Redirect URI:</span>{" "}
                  <code className="font-mono break-all">{detailsQuery.data.redirectUri}</code>
                </p>
                {detailsQuery.data.client.softwareId ? (
                  <p className="mt-1">
                    <span className="font-medium text-slate-800">Software:</span>{" "}
                    {detailsQuery.data.client.softwareId}
                    {detailsQuery.data.client.softwareVersion
                      ? ` (${detailsQuery.data.client.softwareVersion})`
                      : ""}
                  </p>
                ) : null}
              </div>
              {error ? (
                <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
                  {error}
                </div>
              ) : null}
              {handoffComplete ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                  <p className="font-medium">Authorization complete on the hub</p>
                  {redirectTo && isNativeAppOAuthRedirect(redirectTo) ? (
                    <p className="mt-2">
                      Click <strong>Return to Cursor</strong> below. Chrome blocks automatic
                      redirects to Cursor — until you click, Cursor stays on &ldquo;Exchanging
                      token&hellip;&rdquo; and the server will not see{" "}
                      <code className="rounded bg-emerald-100 px-1 font-mono text-xs">POST /oauth/token</code>.
                    </p>
                  ) : (
                    <p className="mt-1">
                      Return to your MCP client. If it does not reconnect automatically, use the
                      button below.
                    </p>
                  )}
                  <div className="mt-3">
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => {
                        if (!redirectTo) return;
                        mcpOAuthLog("consent_handoff_user_click", {
                          clientId: params.client_id,
                          redirectScheme: safeRedirectScheme(redirectTo),
                        });
                        window.location.href = redirectTo;
                      }}
                    >
                      Return to Cursor
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => void decide("deny")}
                    isLoading={submitting === "deny"}
                    disabled={submitting !== null}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => void decide("approve")}
                    isLoading={submitting === "approve"}
                    disabled={submitting !== null}
                  >
                    Approve
                  </Button>
                </div>
              )}
              <p className="text-xs text-slate-500">
                You can revoke this authorization at any time from{" "}
                <a className="text-brand-700 underline" href="/personal/chatgpt">
                  /integrations/mcp
                </a>
                .
              </p>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
